import { ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { Box, Button, Center, Group, Loader, rem, SegmentedControl, Select, SimpleGrid, Text, Tooltip } from "@mantine/core";
import { SVGProps } from "react";
import { PersonCard } from "../../../components/common/PersonCard";
import { Person, SortDirection } from "../../../models/person.model";
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';

const IconChevronUp = (props: SVGProps<SVGSVGElement>) => (
    <ChevronUpIcon {...props} />
);
const IconChevronDown = (props: SVGProps<SVGSVGElement>) => (
    <ChevronDownIcon {...props} />
);
const IconSquares2X2 = (props: SVGProps<SVGSVGElement>) => (
    <Squares2X2Icon {...props} />
);
const IconListBullet = (props: SVGProps<SVGSVGElement>) => (
    <ListBulletIcon {...props} />
);

interface CVListGridProps {
    persons: (Person & { lastUpdated: string })[];
    loading: boolean;
    searchDuration: number;
    totalItems: number;
    sortField: string | null;
    sortDirection: SortDirection;
    selectedPersonId: number | null;
    currentKeywords: string[];
    pageSize: number;
    isFiltered: boolean;
    viewMode: "grid" | "list";
    onSortChange: (field: string) => void;
    onPageSizeChange: (value: string) => void;
    onViewModeChange: (value: string) => void;
    onSelectPerson: (person: Person) => void;
    onModifyPerson: (e: React.MouseEvent, personId: number) => void;
    onActivityChange: (e: React.MouseEvent, person: Person) => void;
    onDeletePerson: (e: React.MouseEvent, person: Person) => void;
    onCopyToProposal: (id: number) => void;
    onClearSearch: () => void;
    onRefresh?: () => void;
    selectionMode?: boolean;
    selectedIds?: Set<number>;
    onToggleSelect?: (id: number) => void;
}

export function CVListGrid({
    persons,
    loading,
    searchDuration,
    totalItems,
    sortField,
    sortDirection,
    selectedPersonId,
    currentKeywords,
    pageSize,
    isFiltered,
    viewMode,
    onSortChange,
    onPageSizeChange,
    onViewModeChange,
    onSelectPerson,
    onModifyPerson,
    onActivityChange,
    onDeletePerson,
    onCopyToProposal,
    onClearSearch,
    onRefresh,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
}: CVListGridProps) {
    return (
        <>
            {/* ── Sort / page-size bar ── */}
            <Group mb="sm" justify="space-between" wrap="wrap" gap="xs">
                <Group gap="xs" wrap="wrap">
                    <Text size="sm" fw={500}>
                        Sort by:
                    </Text>
                    <Group gap="xs" wrap="wrap">
                        {[
                            { id: "none", label: "Don't sort" },
                            { id: "firstName", label: "First Name" },
                            { id: "lastName", label: "Last Name" },
                            { id: "cvUpdateDate", label: "CV Update Date" },
                        ].map((sortOption) => (
                            <Button
                                key={sortOption.id}
                                size="xs"
                                variant={
                                    sortField === sortOption.id ||
                                        (sortOption.id === "none" && sortField === null)
                                        ? "filled"
                                        : "subtle"
                                }
                                color={
                                    sortField === sortOption.id ||
                                        (sortOption.id === "none" && sortField === null)
                                        ? "brand"
                                        : "gray"
                                }
                                onClick={() => onSortChange(sortOption.id)}
                                rightSection={
                                    sortField === sortOption.id ? (
                                        sortDirection === SortDirection.Ascending ? (
                                            <IconChevronUp style={{ width: rem(14) }} />
                                        ) : (
                                            <IconChevronDown style={{ width: rem(14) }} />
                                        )
                                    ) : null
                                }
                            >
                                {sortOption.label}
                            </Button>
                        ))}
                    </Group>
                </Group>

                <Group gap="xs" align="flex-end">
                    <Select
                        size="xs"
                        value={pageSize.toString()}
                        onChange={(value) => value && onPageSizeChange(value)}
                        data={[
                            { value: "5", label: "5 per page" },
                            { value: "10", label: "10 per page" },
                            { value: "20", label: "20 per page" },
                            { value: "50", label: "50 per page" },
                        ]}
                        disabled={isFiltered}
                        style={{ minWidth: 110 }}
                    />
                    {/* ── View toggle ── */}
                    <SegmentedControl
                        value={viewMode}
                        onChange={onViewModeChange}
                        aria-label="Choose view mode"
                        size="xs"
                        color="brand"
                        data={[
                            {
                                value: "grid",
                                label: (
                                    <Tooltip label="Card view" withArrow position="bottom">
                                        <Center>
                                            <IconSquares2X2
                                                style={{ width: rem(18), height: rem(18) }}
                                            />
                                        </Center>
                                    </Tooltip>
                                ),
                            },
                            {
                                value: "list",
                                label: (
                                    <Tooltip label="List view" withArrow position="bottom">
                                        <Center>
                                            <IconListBullet
                                                style={{ width: rem(18), height: rem(18) }}
                                            />
                                        </Center>
                                    </Tooltip>
                                ),
                            },
                        ]}
                    />
                </Group>
            </Group>

            <SimpleGrid
                cols={{
                    base: 1,
                    sm: selectedPersonId ? 1 : 2,
                    md: selectedPersonId ? 2 : 3,
                    lg: selectedPersonId ? 3 : 4,
                    xl: selectedPersonId ? 3 : 5,
                }}
                spacing="lg"
                p="xs"
                style={{ minHeight: '60vh', alignContent: 'start' }}
            >
                {loading ? (
                    <>
                        <Box
                            p="xl"
                            style={{ gridColumn: "1 / -1", textAlign: "center" }}
                        >
                            <Loader size="md" color="brand" />
                            <Text mt="md">Loading results...</Text>
                            {searchDuration > 10 && (
                                <Text size="sm" c="dimmed" mt="xs">
                                    This may take a moment. Search has been running for{" "}
                                    {searchDuration} seconds.
                                </Text>
                            )}
                        </Box>
                        {persons.length > 0 && (
                            <Box
                                p="md"
                                style={{ gridColumn: "1 / -1", textAlign: "center" }}
                            >
                                <Text size="sm" c="dimmed">
                                    Showing previous results while searching...
                                </Text>
                            </Box>
                        )}
                    </>
                ) : persons.length === 0 ? (
                    <Box
                        p="xl"
                        style={{ gridColumn: "1 / -1", textAlign: "center" }}
                    >
                        <Text size="lg" fw={500} c="dimmed">
                            No CVs found matching your search criteria
                        </Text>
                        {totalItems > 0 && (
                            <Text size="sm" c="orange" mt="xs">
                                Note: {totalItems} results were found by the API, but they
                                were filtered out by your current filters
                            </Text>
                        )}
                        <Text size="sm" c="dimmed" mt="md">
                            Try adjusting your search terms
                        </Text>
                        <Group justify="center" mt="md">
                            <Button
                                variant="subtle"
                                color="brand"
                                size="xs"
                                onClick={onClearSearch}
                                disabled={currentKeywords.length === 0}
                            >
                                Clear search terms
                            </Button>
                        </Group>
                    </Box>
                ) : (
                    persons.map((person) => (
                        <PersonCard
                            key={person.id}
                            id={person.id}
                            name={`${person.firstName} ${person.lastName}`}
                            title={person.positions?.[0] || ""}
                            email={person.email}
                            profilePhoto={typeof person.image === 'string' ? person.image : undefined}
                            lastUpdated={person.lastUpdated}
                            isUnassigned={isPersonCurrentlyUnassigned(person.availabilityDate)}
                            isInactive={person.inactive}
                            hasAvailabilityDate={!!person.availabilityDate}
                            availableDate={person.availabilityDate}
                            finalScoring={person.finalScoring}
                            officeLocation={person.officeLocationName}
                            onDownloadCV={(format) => { }}
                            onCopyToProposal={() => onCopyToProposal(person.id)}
                            onClick={() => onSelectPerson(person)}
                            onModify={(e) => onModifyPerson(e, person.id)}
                            onActivityChange={(e) => onActivityChange(e, person)}
                            onDelete={(e) => onDeletePerson(e, person)}
                            person={person}
                            useConnectedActions={true}
                            onRefresh={onRefresh}
                            selectionMode={selectionMode}
                            isSelected={selectedIds?.has(person.id)}
                            onToggleSelect={onToggleSelect}
                        />
                    ))
                )}
            </SimpleGrid>
        </>
    );
}

